/**
 * ITEM-ACTIVE.4b — the app's status dialect, in one place.
 *
 * These exact values already existed as STATUS_CONFIG inside the inventory
 * list, where they colour the Out / Low / OK / Reserved pills. The lifecycle
 * chip now speaks the same dialect: a live item reads in the OK family, a
 * retired one in the Out family.
 *
 * Extracted rather than copied because the chip renders on two surfaces — the
 * item header and the list — and two hand-kept copies of a palette drift. The
 * list still owns the STOCK statuses; this module owns the tokens both of them
 * read.
 *
 * WHY NO GREY. Active was previously the neutral tint, which made the ordinary
 * case invisible on a row already carrying coloured stock pills: the eye read
 * the greens and reds and never picked up the one grey chip. Joining the
 * dialect means a reader learns one colour language for the whole row.
 */

export interface StatusTone {
  color: string;
  bg: string;
}

/** The dialect. Values are the ones the list has always used. */
export const STATUS_TONES = {
  OUT: { color: '#cf222e', bg: '#ffebe9' },
  LOW: { color: '#9a6700', bg: '#fef9c3' },
  OK: { color: '#1a7f37', bg: '#dafbe1' },
  RESERVED: { color: '#8250df', bg: '#f5f0ff' },
} as const;

/**
 * Lifecycle, in the same language:
 *   active  → the OK family, because a live item is the healthy case
 *   retired → the Out family, which is what it has read as since .1
 */
export const lifecycleTone = (active: boolean): StatusTone =>
  (active ? STATUS_TONES.OK : STATUS_TONES.OUT);
